import {
  FC,
  ForwardRefExoticComponent,
  forwardRef,
  ReactNode,
  RefAttributes,
} from "react";
import {
  useSelect,
  UseSelectProps,
  UseSelectState,
  UseSelectStateChangeOptions,
} from "downshift";
import cn from "classnames";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import { extendGetter } from "./utils";
import DropdownContext from "./dropdown-context";
import DropdownContextComponent, {
  DropdownContextComponentProps,
} from "./dropdown-context/dropdown-context-component";
import DropdownLabel, { DropdownLabelProps } from "./dropdown-label";
import DropdownToggle, { DropdownToggleProps } from "./dropdown-toggle";
import DropdownIcon, { DropdownIconProps } from "./dropdown-icon";
import DropdownMenu, { DropdownMenuProps } from "./dropdown-menu";
import DropdownItem, { DropdownItemProps } from "./dropdown-item";
import { Variant, StyleType } from "./enum";

import "./dropdown.scss";

export interface Item {
  disabled?: boolean;
  id?: string;
  isDivider?: boolean;
  label?: string;
  isSelected?: boolean;
  isSelectable?: boolean;

  [key: string]: any;
}

export interface DropdownProps {
  items: Item[];
  className?: string;
  styleType?: keyof typeof StyleType;
  disabled?: boolean;
  showAllItems?: boolean;
  downshiftOptions?: Partial<UseSelectProps<Item>>;
  matchToggleWidth?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  variant?: keyof typeof Variant;
  children: ReactNode;
  isLoading?: boolean;
  announceLoadingStart?: string;
  announceLoadingEnd?: string;
  loadingPlaceholder?: string;
  isMultiselect?: boolean;
}

export interface DropdownComposite
  extends ForwardRefExoticComponent<
    DropdownProps & RefAttributes<HTMLDivElement>
  > {
  Context: FC<DropdownContextComponentProps>;
  Label: FC<DropdownLabelProps>;
  Toggle: FC<DropdownToggleProps>;
  Menu: FC<DropdownMenuProps>;
  Item: FC<DropdownItemProps>;
  Icon: FC<DropdownIconProps>;
}

const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      items,
      className: consumerBlockClassName = "",
      styleType = StyleType.primary,
      disabled = false,
      placeholder,
      downshiftOptions = {},
      matchToggleWidth = false,
      showAllItems = false,
      hideLabel = false,
      variant = Variant.LISTBOX,
      children,
      isLoading = false,
      announceLoadingStart,
      announceLoadingEnd,
      loadingPlaceholder,
      isMultiselect = false,
    }: DropdownProps,
    ref,
  ) => {
    const getBlocksWith = createBemBlockBuilder([
      "eb-dropdown",
      consumerBlockClassName,
    ]);

    function stateReducer(
      state: UseSelectState<Item>,
      actionAndChanges: UseSelectStateChangeOptions<Item>,
    ) {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownCharacter:
          return variant === Variant.MENUBUTTON ? state : changes; // ignore this event for menu button

        case useSelect.stateChangeTypes.ItemClick:
        case useSelect.stateChangeTypes.MenuKeyDownEnter:
        case useSelect.stateChangeTypes.MenuKeyDownSpaceButton: {
          //if item is divider and not selectable, we should ignore this event (mouse click/press enter/press space)
          if (isMultiselect) {
            return changes?.selectedItem?.isDivider &&
              !changes?.selectedItem?.isSelectable
              ? state
              : {
                  ...changes,
                  isOpen: true,
                  highlightedIndex: state.highlightedIndex,
                };
          }

          return changes?.selectedItem?.isDivider &&
            !changes?.selectedItem?.isSelectable
            ? state
            : changes;
        }

        case useSelect.stateChangeTypes.ItemMouseMove: {
          //if item is divider and not selectable, we should ignore this event (mouse over)
          // @ts-expect-error changes can never be null or undefined
          const item = items[changes.highlightedIndex];
          const isDivider = item && item.isDivider && !item.isSelectable;
          return isDivider ? { ...state, highlightedIndex: -1 } : changes;
        }
        case useSelect.stateChangeTypes.MenuKeyDownArrowDown: {
          //if an item is a divider, and it's the first item in the list,
          //we should ignore them and go straight to the second element
          //and if it is the last element, we should ignore it and not allow the user to click down unnecessarily
          // @ts-expect-error changes can never be null or undefined
          const item = items[changes.highlightedIndex];
          const isDivider = item && item.isDivider && !item.isSelectable;
          const elementIndex = items.findIndex((el) => el === item);
          const firstItem = elementIndex === 0;
          const nextItem = items
            .slice(elementIndex)
            .find((el) => !el.isDivider);
          const lastItem = !nextItem;
          const nextItemIndex = items.findIndex((el) => el === nextItem);

          if (isDivider && firstItem) {
            return {
              //ignore divider and go to next item, if divider a first item
              ...changes,
              highlightedIndex: nextItemIndex,
            };
          }

          if (isDivider && lastItem) {
            //and if divider is the last element, we should ignore it and not allow the user to click down unnecessarily
            return state;
          }
          return changes;
        }
        case useSelect.stateChangeTypes.MenuKeyDownArrowUp: {
          //if an item is a divider, and it's the first element and not selectable,
          //we should ignore it and not allow the user to click up unnecessarily
          // @ts-expect-error changes can never be null or undefined
          const item = items[changes.highlightedIndex];
          const isDivider = item && item.isDivider && !item.isSelectable;
          const elementIndex = items.findIndex((el) => el === item);

          return isDivider && elementIndex === 0 ? state : changes; // ignore this events for dividers
        }
        default:
          return changes; // otherwise business as usual.
      }
    }

    const menuButtonFocus =
      variant === Variant.MENUBUTTON
        ? {
            selectedItem: null,
            defaultHighlightedIndex: items && items.length && items[0] ? 0 : -1,
          }
        : {};

    const {
      getToggleButtonProps: downshiftGetToggleButtonProps,
      getItemProps: downshiftGetItemProps,
      getLabelProps: downshiftGetLabelProps,
      getMenuProps: downshiftGetMenuProps,
      ...downshiftProps
    } = useSelect({
      items,
      stateReducer,
      itemToString: (item) => (item && item.label) || "",
      initialSelectedItem:
        (!placeholder && items && items.length && items[0]) || undefined,
      ...menuButtonFocus,
      ...downshiftOptions,
    });

    const isOpen = downshiftProps.isOpen && !isLoading;

    const useMenuButtonAriaAttributes = (attributes: object): object | null =>
      variant === Variant.MENUBUTTON ? attributes : null;

    const getToggleButtonProps = extendGetter(downshiftGetToggleButtonProps, {
      className: cn(
        getBlocksWith("__toggle-button"),
        getBlocksWith(`__toggle-button--${styleType}`),
        disabled && getBlocksWith("__toggle-button--disabled"),
        isOpen && getBlocksWith("__toggle-button--open"),
      ),
      disabled,
      ...useMenuButtonAriaAttributes({
        "aria-haspopup": "menu",
        "aria-controls": downshiftGetMenuProps().id,
      }),
    });

    const getItemProps = extendGetter(downshiftGetItemProps, {
      className: cn(
        getBlocksWith("__item"),
        getBlocksWith(`__item--${styleType}`),
      ),
      ...useMenuButtonAriaAttributes({
        role: "menuitem",
        "aria-selected": undefined,
      }),
    });

    const getLabelProps = extendGetter(downshiftGetLabelProps, {
      className: cn(
        getBlocksWith("__label"),
        getBlocksWith(`__label--${styleType}`),
        hideLabel && getBlocksWith("__label--hidden"),
      ),
    });

    const getMenuProps = extendGetter(downshiftGetMenuProps, {
      className: cn(
        getBlocksWith("__menu"),
        getBlocksWith(`__menu--${styleType}`),
        matchToggleWidth && getBlocksWith("__menu--narrow"),
        showAllItems && getBlocksWith("__menu--show-all"),
        isOpen && getBlocksWith("__menu--open"),
      ),
      ...useMenuButtonAriaAttributes({
        role: "menu",
      }),
    });

    const genericGetter = <T extends object>(props: T): T => ({
      ...props,
    });

    const getDividerProps = extendGetter(downshiftGetItemProps, {
      role: "separator",
      "aria-selected": undefined,
      "aria-disabled": true,
      "aria-labelledby": null,
      className: cn(
        getBlocksWith("__divider"),
        getBlocksWith(`__divider--${styleType}`),
      ),
    });

    const getSelectableDividerProps = extendGetter(downshiftGetItemProps, {
      className: cn(
        getBlocksWith("__selectable_divider"),
        getBlocksWith(`__selectable_divider--${styleType}`),
      ),
      ...useMenuButtonAriaAttributes({
        role: "menuitem",
        "aria-disabled": false,
        "aria-selected": undefined,
      }),
    });

    const getIconProps = extendGetter(genericGetter, {
      className: cn(
        getBlocksWith("__icon"),
        getBlocksWith(`__icon--${styleType}`),
      ),
      role: "presentation",
    });

    const dropdownProps = {
      getBlocksWith,
      getToggleButtonProps,
      getItemProps,
      getDividerProps,
      getLabelProps,
      getSelectableDividerProps,
      getMenuProps,
      getIconProps,
      placeholder,
      variant,
      isLoading,
      isMultiselect,
      announceLoadingStart,
      announceLoadingEnd,
      loadingPlaceholder,
      ...downshiftProps,
      isOpen,
    };

    return (
      <DropdownContext.Provider value={dropdownProps}>
        <div
          className={cn(getBlocksWith(), getBlocksWith(`--${styleType}`))}
          data-auto="dropdown"
          ref={ref}
        >
          {children}
        </div>
      </DropdownContext.Provider>
    );
  },
) as DropdownComposite;

Dropdown.Context = DropdownContextComponent;
Dropdown.Label = DropdownLabel;
Dropdown.Toggle = DropdownToggle;
Dropdown.Icon = DropdownIcon;
Dropdown.Menu = DropdownMenu;
Dropdown.Item = DropdownItem;

Dropdown.displayName = "Dropdown";

export default Dropdown;
